import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Header from '../Header'
import FooterSimple from '../FooterSimple'

function TermsConditions() {
  return (
    <div>
      <Helmet>
        <title>Terms &amp; Conditions — Redi Platforms</title>
        <meta
          name="description"
          content="Terms and conditions for Redi Platforms, including our SMS messaging program."
        />
      </Helmet>

      <Header />

      <div className="container my-5 legal-doc-page">
        <h1 className="mb-2">Terms &amp; Conditions</h1>
        <p className="text-muted small mb-4">
          Last updated: April 14, 2026 &middot; Redi Platforms, LLC
        </p>

        <p className="lead legal-doc-lead">
          These terms explain how we communicate with you by text message and
          set a few simple ground rules for our relationship. We have written
          them in plain language so they are easy to understand. If you have
          questions, please reach out using the contact information at the end
          of this page.
        </p>

        <section className="legal-doc-section rc-verbatim-highlight">
          <p>
            1- SMS Consent Communication:
            <br />
            <br />
            The information (Phone Numbers) obtained as part of the SMS consent
            process will not be shared with third parties for marketing purposes.
          </p>
          <p>
            2- Types of SMS Communications:
            <br />
            <br />
            If you have consented to receive text messages from Redi Platforms
            LLC, you may receive messages related to the following (provide
            specific examples):
            <br />
            <br />
            Appointment reminders
            <br />
            Follow-up messages
            <br />
            Billing inquiries
          </p>
          <p>
            3- Message Frequency:
            <br />
            Message frequency may vary depending on the type of communication.
            For example, you may receive up to five SMS messages per week
            related to your [appointments/billing, etc.].
          </p>
          <p>
            4- Potential Fees for SMS Messaging:
            <br />
            <br />
            Please note that standard message and data rates may apply, depending
            on your carrier’s pricing plan. These fees may vary if the message
            is sent domestically or internationally.
          </p>
          <p>
            5- Opt-In Method:
            <br />
            <br />
            You may opt-in to receive SMS messages from Redi Platforms LLC in
            the following ways (This is an example. Please include the method
            used.)
            <br />
            <br />
            Verbally, during a conversation
            <br />
            By submitting an online form
            <br />
            By filling out a paper form
          </p>
          <p>
            6- Opt-Out Method:
            <br />
            <br />
            You can opt out of receiving SMS messages at any time. To do so,
            simply reply "STOP" to any SMS message you receive. Alternatively,
            you can contact us directly to request removal from our messaging
            list.
          </p>
          <p>
            7- Help:
            <br />
            <br />
            If you are experiencing any issues, you can reply with the keyword
            HELP. Or, you can get help directly from us at
            https://maderedi.com/.
          </p>
          <p>
            Additional Options:
          </p>
          <p>
            8- Standard Messaging Disclosures:
            <br />
            <br />
            Message and data rates may apply.
            <br />
            You can opt out at any time by texting "STOP."
            <br />
            For assistance, text "HELP" or visit our [Privacy Policy] and [Terms
            and Conditions] pages.
            <br />
            Message frequency may vary
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>1. Who we are</h2>
          <p>
            &ldquo;Redi Platforms,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; and
            &ldquo;our&rdquo; refer to Redi Platforms, LLC, a Delaware limited
            liability company. We operate a family of businesses and products
            (including, for example, Lumina, Clermont, Redi To Post, and Redi
            PropTech) that serve customers, partners, and investors.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>2. SMS messaging program</h2>
          <p>
            When you choose to receive SMS messages from us, you are signing up
            only for text messages from Redi Platforms and the brands we
            identify in the signup flow. We use SMS to share helpful updates
            with you—for example, <strong>informational</strong> messages such as
            service notices, account-related reminders, and operational alerts,
            and sometimes <strong>promotional</strong> messages such as company
            news, product highlights, or invitations that we believe are
            relevant to you. If you are an investor who has opted in separately,
            you may also receive messages about company progress and business
            activities, consistent with that opt-in.
          </p>
          <p>
            <strong>Consent.</strong> You agree to receive SMS messages only when
            you provide your mobile number and complete a clear, separate
            opt-in for SMS (for example, through our website or another
            sign-up flow we designate). Your SMS consent is not bundled with
            consent for email, phone calls, or other channels unless we offer
            those choices separately and you choose them on their own.
          </p>
          <p>
            <strong>Message frequency and charges.</strong> Messaging frequency
            may vary. Message and data rates may apply.
          </p>
          <p>
            <strong>How to opt out and get help.</strong> To opt out at any
            time, text <strong>STOP</strong>. For assistance, text{' '}
            <strong>HELP</strong> or visit our website at{' '}
            <a href="https://maderedi.com/" target="_blank" rel="noreferrer">
              https://maderedi.com/
            </a>
            . After you opt out, you may receive one final text confirming that
            we received your request.
          </p>
          <p>
            <strong>Carriers.</strong> Participating carriers are supported for
            standard U.S. mobile messaging. Wireless carriers are not liable for
            delayed or undelivered messages. Delivery can depend on your device,
            coverage, and carrier systems.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>3. Privacy and your information</h2>
          <p>
            We take your privacy seriously. Our{' '}
            <Link to="/privacy">Privacy Policy</Link> describes how we collect,
            use, and share personal information—including in connection with SMS.
            We do not sell your SMS opt-in or your phone number collected for SMS
            to third parties for their own marketing. We may use trusted service
            providers to help deliver messages; they may use your information
            only as needed to provide those services to us.
          </p>
          <p>
            Visit{' '}
            <a href="https://maderedi.com/privacy" target="_blank" rel="noreferrer">
              https://maderedi.com/privacy
            </a>{' '}
            for our Privacy Policy and{' '}
            <a href="https://maderedi.com/terms" target="_blank" rel="noreferrer">
              https://maderedi.com/terms
            </a>{' '}
            for these Terms &amp; Conditions.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>4. Eligibility</h2>
          <p>
            Our SMS program is intended for users who are at least 18 years old
            and who provide a valid U.S. mobile number. By opting in, you
            confirm that you meet these requirements and that you are the
            subscriber or customary user of the phone number you provide.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>5. Accuracy and availability</h2>
          <p>
            We aim to keep our messages accurate and timely. Occasionally,
            information may change after a message is sent, or a message may be
            delayed. If something looks wrong or you need clarification, please
            contact us—we are happy to help.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>6. Changes to these terms</h2>
          <p>
            We may update these Terms &amp; Conditions from time to time,
            including to reflect changes in our SMS program or legal
            requirements. When we make material changes, we will take reasonable
            steps to let you know—for example, by posting an updated version
            here with a new &ldquo;Last updated&rdquo; date or, where appropriate,
            by sending you a notice by SMS or email. Your continued participation
            in our SMS program after changes become effective constitutes your
            acceptance of the updated terms, except where applicable law requires
            additional steps.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>7. Contact us</h2>
          <p>
            Questions about these terms or our SMS program? Please email us at{' '}
            <a href="mailto:elie@maderedi.com">elie@maderedi.com</a>. We will do
            our best to respond promptly.
          </p>
        </section>

        <section className="legal-doc-section">
          <h2>8. General</h2>
          <p>
            These terms apply to our SMS messaging program as described here.
            Other agreements (for example, product-specific terms or investor
            documents) may also apply where you have entered into them
            separately. If any part of these terms is found unenforceable, the
            remaining provisions stay in effect. These terms are governed by the
            laws of the State of Delaware, excluding its conflict-of-law rules,
            except where your jurisdiction requires otherwise.
          </p>
        </section>
      </div>

      <FooterSimple />
    </div>
  )
}

export default TermsConditions
